import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import useIsMobile from "../../Hook/mobile";
import { displayPopin, useListenPopins } from "../../Hook/popins";
import { dismiss, isDismissed, scopedKey } from "../../Hook/storage";
import useUser from "../../Hook/user";

const DISMISS_DURATION_SECONDS = 60 * 60;

/**
 * The offer-code prompt (Shared chrome board, turn 6b): it waits rather than
 * interrupts. A bottom sheet above the tab bar on mobile, a corner card above
 * the chat docks on desktop — no scrim either way, Shell tokens instead of the
 * old blue gradient, and the toast slot (top-right) stays untouched.
 *
 * Behaviour is unchanged: members only, hidden when missing or expired, one
 * primary that opens the subscribe popin with the code attached, and dismissal
 * persists an hour under the per-code key. The discount figure renders only
 * when the payload carries one; the code shows verbatim, monospace.
 */
function OfferCodePrompt() {
  const user = useUser();
  const isMobile = useIsMobile();
  const display = displayPopin();
  // Collision rule: while any popin is open the prompt waits, and shows itself
  // again the moment the stack empties.
  const openPopins = useListenPopins();

  const offer = user?.offerCode;
  const code = offer?.code?.trim();
  const isExpired =
    typeof offer?.time_expiration === "number" &&
    offer.time_expiration > 0 &&
    offer.time_expiration < Date.now() / 1000;
  const storageKey = code ? scopedKey(`offercode:dismissed:${code}`) : "";
  // Derived, not mirrored (CL7): storage is the source of truth, the nonce only
  // forces a re-read after this session writes it.
  const [dismissNonce, setDismissNonce] = useState(0);
  const dismissed = useMemo(
    () => !!storageKey && isDismissed(storageKey),
    [storageKey, dismissNonce],
  );

  if (!user?.informations) return null;
  if (user.informations.type === "guest") return null;
  if (!code || isExpired || dismissed) return null;
  if (openPopins.length > 0) return null;

  const close = () => {
    dismiss(storageKey, DISMISS_DURATION_SECONDS);
    setDismissNonce((n) => n + 1);
  };

  const displaySub = () => {
    display("subscribe", { code });
    close();
  };

  const minutesLeft =
    typeof offer?.time_expiration === "number" && offer.time_expiration > 0
      ? Math.max(1, Math.round((offer.time_expiration - Date.now() / 1000) / 60))
      : null;

  const body = (
    <>
      <div className="flex items-start gap-3">
        {!!offer?.pc_discount && (
          <div className="text-[28px] font-semibold leading-none text-accent flex-none">
            -{offer.pc_discount}%
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-card-title font-medium text-text">
            <FormattedMessage
              id="offer_code_prompt__title"
              defaultMessage="Subscription discount"
            />
          </div>
          <div className="text-meta text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span>
              <FormattedMessage id="offer_code_prompt__code" defaultMessage="Code" />
            </span>
            {/* Verbatim: same case, same characters, monospace, never truncated. */}
            <span className="font-mono tracking-[0.08em] text-text bg-well rounded-md px-1.5 py-0.5">
              {code}
            </span>
            {minutesLeft !== null && (
              <span>
                ·{" "}
                <FormattedMessage
                  id="offer_code_prompt__expires"
                  defaultMessage="expires in {minutes} min"
                  values={{ minutes: minutesLeft }}
                />
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="close"
          data-testid="offer-code-dismiss"
          className="flex-none w-7 h-7 rounded-pill text-muted flex items-center justify-center hover:bg-tint-hover"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <button
        type="button"
        onClick={displaySub}
        data-testid="offer-code-apply"
        className="w-full min-h-[44px] px-4 py-2 rounded-pill bg-tint-apply-bg text-tint-apply-fg text-row font-medium"
      >
        <FormattedMessage id="offer_code_prompt__apply" defaultMessage="Apply the offer" />
      </button>
    </>
  );

  if (isMobile.isMobile) {
    // A sheet without a scrim: the page stays usable, the tab bar stays clear.
    return (
      <div className="fixed inset-x-0 bottom-bottom-nav z-sheet-low" data-testid="offer-code-prompt">
        <div className="s4s-sheet bg-panel rounded-t-sheet shadow-sheet px-4 pt-3.5 pb-4 flex flex-col gap-3">
          {body}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed right-8 bottom-24 z-sheet-low w-[320px]"
      data-testid="offer-code-prompt"
    >
      <div className="s4s-popin bg-panel rounded-panel shadow-sheet p-4 flex flex-col gap-3">
        {body}
      </div>
    </div>
  );
}

export default OfferCodePrompt;
